import React, { useState, useEffect } from 'react';
import VideoUploader from '../components/VideoUploader';
import VideoProcessing from '../components/VideoProcessing';
import ViralClips from '../components/ViralClips';
import { useSelector } from 'react-redux';
import { selectCompletedUploads } from '../app/redux/slices/uploadsSlice';
import './Home.css';
import TranscriptionService from '../services/transcriptionService';
import { processVideoWithStages } from '../services/videoProcessing';

const API_URL =  'https://app.polarcut.io/api';
const Home = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedClips, setProcessedClips] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState('Analyzing video content');
  const completedUploads = useSelector(selectCompletedUploads);
  const [shouldStartProcessing, setShouldStartProcessing] = useState(false);

  const handleProcessingStart = () => {
    setShouldStartProcessing(true);
  };

  const checkTranscriptionStatus = async (jobId, retryCount = 0, maxRetries = 3) => {
    try {
      setProcessingStage('Transcribing audio');
      setProcessingProgress(25); // Start from 25% for transcription

      // Use a ref to track if we should stop polling
      let shouldContinuePolling = true;
      
      const fetchStatus = async (currentRetry = 0) => {
        // Check if we should still be polling
        if (!shouldContinuePolling) return;
        
        try {
          const response = await fetch(`${API_URL}/transcribe/status/${jobId}`);
          const data = await response.json();
          console.log("response============>",data.data.TranscriptionJobStatus);

          if (response.ok && data.status === 'success') {
            const transcriptionStatus = data.data.TranscriptionJobStatus;
            
            if (transcriptionStatus === 'COMPLETED') {
              // Stop polling once complete
              shouldContinuePolling = false;
              setProcessingStage('Transcription complete');
              setProcessingProgress(50);
              // Proceed to the next step (e.g., process the transcript)
              checkClipsMetaStatus(jobId);
            } else if (transcriptionStatus === 'FAILED') {
              // Stop polling on failure
              shouldContinuePolling = false;
              console.error('Transcription job failed');
              setProcessingStage('Transcription failed');
            } else {
              // Job is still in progress, continue polling
              setProcessingProgress((prev) => Math.min(45, prev + 1));
              if (shouldContinuePolling) {
                setTimeout(fetchStatus, 10000);
              }
            }
          } else if (response.status === 404) {
            // Job not found - implement retry logic
            if (currentRetry < maxRetries) {
              console.log(`Transcription job not found, retrying (${currentRetry + 1}/${maxRetries})...`);
              setProcessingStage(`Waiting for transcription job (retry ${currentRetry + 1}/${maxRetries})`);
              if (shouldContinuePolling) {
                setTimeout(() => fetchStatus(currentRetry + 1), 10000);
              }
            } else {
              // Stop polling after max retries
              shouldContinuePolling = false;
              console.error('Transcription job not found after maximum retries');
              setProcessingStage('Transcription job not found');
            }
          } else {
            // Stop polling on other errors
            shouldContinuePolling = false;
            console.error('Error checking transcription status:', data.message);
            setProcessingStage('Error checking transcription');
          }
        } catch (error) {
          console.error('Error fetching transcription status:', error);
          
          // Also implement retry for network errors
          if (currentRetry < maxRetries) {
            console.log(`Error fetching status, retrying (${currentRetry + 1}/${maxRetries})...`);
            setProcessingStage(`Connection error, retrying (${currentRetry + 1}/${maxRetries})`);
            if (shouldContinuePolling) {
              setTimeout(() => fetchStatus(currentRetry + 1), 10000);
            }
          } else {
            // Stop polling after max retries
            shouldContinuePolling = false;
            setProcessingStage('Failed to check transcription status');
          }
        }
      };

      // Start the polling process
      fetchStatus(0);
      
      // Clean up function to stop polling if component unmounts
      return () => {
        shouldContinuePolling = false;
      };
    } catch (error) {
      console.error('Error initiating transcription status check:', error);
      setProcessingStage('Error initiating status check');
    }
  };

  // Separate function for finding clips meta
  const findClipsMeta = async (fileId, maxAttempts = 30, attemptDelay = 10000) => {
    setProcessingStage('Finding viral clips');
    setProcessingProgress(50);
    
    let attempts = 0;
    let clipsMetaFound = false;
    
    while (!clipsMetaFound && attempts < maxAttempts) {
      try {
        console.log(`Attempt ${attempts + 1}/${maxAttempts} to find clips meta for ${fileId}`);
        const response = await fetch(`${API_URL}/status/clips_meta/${fileId}`);
        const data = await response.json();
        
        if (response.ok && data.status === 'success') {
          console.log('Clips meta found successfully!');
          clipsMetaFound = true;
          return true; // Clips meta found successfully
        } else if (response.status === 404) {
          // Clips meta not found yet, continue trying
          console.log('Clips meta not found yet, continuing to poll...');
          attempts++;
          setProcessingProgress((prev) => Math.min(70, 50 + attempts));
          await new Promise(resolve => setTimeout(resolve, attemptDelay));
        } else {
          // Other error occurred
          console.error('Error in clips meta check:', data.message);
          attempts++;
          await new Promise(resolve => setTimeout(resolve, attemptDelay));
        }
      } catch (error) {
        console.error('Error connecting to clips meta API:', error);
        attempts++;
        await new Promise(resolve => setTimeout(resolve, attemptDelay));
      }
    }
    
    if (attempts >= maxAttempts) {
      console.error(`Failed to find clips meta after ${maxAttempts} attempts`);
      return false; // Exceeded max attempts
    }
    
    return clipsMetaFound;
  };
  
  // New function to find the actual clips
  const findClips = async (fileId, maxAttempts = 30, attemptDelay = 10000) => {
    setProcessingStage('Retrieving clips');
    setProcessingProgress(75);
    
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      try {
        console.log(`Attempt ${attempts + 1}/${maxAttempts} to find clips for ${fileId}`);
        const clipsResponse = await fetch(`${API_URL}/status/clips/${fileId}`);
        const clipsData = await clipsResponse.json();
        
        if (clipsResponse.ok && clipsData.status === 'success' && 
            clipsData.files && clipsData.files.length >= 3) {
          console.log('Found at least 3 clips!', clipsData.files);
          return clipsData.files; // Return the clips data
        } else if (clipsResponse.status === 404 || 
                  (clipsResponse.ok && (!clipsData.files || clipsData.files.length < 3))) {
          // Clips not found yet or less than 3, continue trying
          console.log(`Found ${clipsData.files?.length || 0} clips, need at least 3, continuing to poll...`);
          // If we have some clips but less than 3, show them with loading state
          if (clipsResponse.ok && clipsData.files && clipsData.files.length > 0) {
            // Set partial clips with loading indicator
            setProcessedClips({
              partialClips: clipsData.files,
              isLoading: true,
              total: 3 // Expected total clips
            });
          }
          attempts++;
          setProcessingProgress((prev) => Math.min(95, 75 + attempts));
          await new Promise(resolve => setTimeout(resolve, attemptDelay));
        } else {
          // Other error occurred
          console.error('Error retrieving clips:', clipsData.message || 'Unknown error');
          attempts++;
          await new Promise(resolve => setTimeout(resolve, attemptDelay));
        }
      } catch (error) {
        console.error('Error connecting to clips API:', error);
        attempts++;
        await new Promise(resolve => setTimeout(resolve, attemptDelay));
      }
    }
    
    console.error(`Failed to find 3 clips after ${maxAttempts} attempts`);
    // Return whatever clips we have, even if less than 3
    try {
      const finalResponse = await fetch(`${API_URL}/status/clips/${fileId}`);
      const finalData = await finalResponse.json();
      if (finalResponse.ok && finalData.files && finalData.files.length > 0) {
        return finalData.files;
      }
    } catch (error) {
      console.error('Final attempt to get clips failed:', error);
    }
    return null;
  };

  // Updated function to check clips meta and then fetch actual clips
  const checkClipsMetaStatus = async (fileId) => {
    try {
      // First, keep trying to find the clips meta until successful or max attempts reached
      const metaFound = await findClipsMeta(fileId);
      
      if (!metaFound) {
        setProcessingStage('Could not find clips meta after multiple attempts');
        return;
      }
      
      // If clips meta was found, keep trying to find the actual clips
      const clipsData = await findClips(fileId);
      
      if (clipsData) {
        setProcessingStage('Clips ready');
        setProcessingProgress(100);
        handleProcessingComplete(clipsData);
      } else {
        setProcessingStage('Failed to retrieve clips after multiple attempts');
      }
    } catch (error) {
      console.error('Error in clips processing workflow:', error);
      setProcessingStage('Processing failed');
    }
  };

  useEffect(() => {
    if (completedUploads && completedUploads.length > 0 && shouldStartProcessing) {
      setIsProcessing(true);
      setProcessingProgress(0);
      setProcessingStage('Starting video processing');
      
      // Use the video processing service instead of the transcription service
      if (completedUploads[0].file) {
        processVideoWithStages(
          completedUploads[0].file,
          (stage, progress) => {
            setProcessingStage(stage);
            setProcessingProgress(progress);
          }
        ).then(clips => {
          handleProcessingComplete(clips);
        }).catch(error => {
          console.error('Error processing video:', error);
          setProcessingStage('Processing failed');
        });
      } else {
        // Fallback to existing logic if no file is available
        checkTranscriptionStatus(completedUploads[0].fileId);
      }
    }
  }, [completedUploads, shouldStartProcessing]);

  const handleProcessingComplete = (clips) => {
    setIsProcessing(false);
    // Pass complete clips with loading state false
    setProcessedClips({
      partialClips: clips,
      isLoading: false,
      total: Math.max(clips.length, 3)
    });
  };

  return (
    <div className="home-page">
      <section className="hero-section">
        <div className="hero-content">
          <h1>Turn Ordinary Videos into Viral Content</h1>
          <p>Our AI analyzes your videos and identifies the most shareable moments that will captivate your audience.</p>
        </div>
      </section>

      <section className="upload-section">
        <h2>Upload Your Video</h2>
        <VideoUploader 
          onProcessingStart={handleProcessingStart}
          onProcessingComplete={handleProcessingComplete}
        />
      </section>

      {isProcessing && (
        <section className="processing-section">
          <VideoProcessing 
            progress={processingProgress} 
            stage={processingStage} 
            isCurrentStageLoading={true}
          />
        </section>
      )}

      {processedClips && (
        <section className="results-section">
          <ViralClips 
            clips={processedClips.partialClips} 
            isLoading={processedClips.isLoading} 
            totalExpected={processedClips.total}
          />
        </section>
      )}
    </div>
  );
};

export default Home;